import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  BarChart3, 
  Settings, 
  FileText, 
  Database,
  Shield,
  LogOut,
  Menu,
  X
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface AdminSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  darkMode: boolean;
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ isOpen, onToggle, darkMode }) => {
  const navigate = useNavigate();
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const menuItems = [
    {
      icon: Users,
      label: 'Estudiantes',
      path: '/admin/dashboard',
      description: 'Gestionar estudiantes'
    }
    // TODO: Implementar otros módulos en el futuro
    // {
    //   icon: BarChart3,
    //   label: 'Estadísticas',
    //   path: '/admin/stats',
    //   description: 'Ver estadísticas'
    // },
    // {
    //   icon: FileText,
    //   label: 'Exámenes',
    //   path: '/admin/exams',
    //   description: 'Gestionar exámenes'
    // },
    // {
    //   icon: Database,
    //   label: 'Base de Datos',
    //   path: '/admin/database',
    //   description: 'Administrar BD'
    // },
    // {
    //   icon: Settings,
    //   label: 'Configuración',
    //   path: '/admin/settings',
    //   description: 'Configurar sistema'
    // }
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/admin/login');
  };

  return (
    <>
      {/* Overlay para móvil */}
      {isOpen && !isDesktop && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 40
          }}
          onClick={onToggle}
        />
      )}

      {/* Sidebar */}
      <motion.div
        initial={false}
        animate={{ 
          x: isDesktop ? 0 : (isOpen ? 0 : -280),
          opacity: isDesktop ? 1 : (isOpen ? 1 : 0)
        }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
        style={{
          position: 'fixed',
          left: 0,
          top: 0,
          height: '100%',
          width: '18rem',
          zIndex: 50,
          backgroundColor: darkMode ? '#111827' : 'white',
          borderRight: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`,
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }}
      >
        {/* Header del Sidebar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '1.5rem',
          borderBottom: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{
              width: '2.5rem',
              height: '2.5rem',
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
              borderRadius: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Shield style={{ color: 'white' }} size={24} />
            </div>
            <div style={{ marginLeft: '0.75rem' }}>
              <h2 style={{
                fontSize: '1.125rem',
                fontWeight: 'bold',
                color: darkMode ? 'white' : '#111827',
                margin: 0
              }}>
                Admin Panel
              </h2>
              <p style={{
                fontSize: '0.75rem',
                color: darkMode ? '#9ca3af' : '#6b7280',
                margin: 0
              }}>
                CHILDREN IBC
              </p>
            </div>
          </div>
          {!isDesktop && (
            <button
              onClick={onToggle}
              style={{
                padding: '0.5rem',
                borderRadius: '0.5rem',
                transition: 'all 0.2s ease',
                backgroundColor: 'transparent',
                color: darkMode ? '#9ca3af' : '#6b7280',
                border: 'none',
                cursor: 'pointer'
              }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <X size={20} />
          </button>
          )}
        </div>

        {/* Navegación */}
        <nav style={{
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem'
        }}>
          {menuItems.map((item, index) => (
            <motion.button
              key={item.path}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => {
                navigate(item.path);
                if (!isDesktop) {
                  onToggle();
                }
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: '0.75rem',
                borderRadius: '0.5rem',
                transition: 'all 0.2s ease',
                backgroundColor: 'transparent',
                color: darkMode ? '#d1d5db' : '#374151',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = darkMode ? '#374151' : '#f3f4f6';
                e.currentTarget.style.color = darkMode ? 'white' : '#111827';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = darkMode ? '#d1d5db' : '#374151';
              }}
            >
              <div style={{
                padding: '0.5rem',
                borderRadius: '0.5rem',
                backgroundColor: darkMode ? '#374151' : '#f3f4f6',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s ease'
              }}>
                <item.icon size={20} />
              </div>
              <div style={{ marginLeft: '0.75rem', textAlign: 'left' }}>
                <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>{item.label}</div>
                <div style={{
                  fontSize: '0.75rem',
                  color: darkMode ? '#9ca3af' : '#6b7280'
                }}>
                  {item.description}
                </div>
              </div>
            </motion.button>
          ))}
        </nav>

        {/* Footer del Sidebar */}
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '1rem',
          borderTop: `1px solid ${darkMode ? '#374151' : '#e5e7eb'}`
        }}>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              padding: '0.75rem',
              borderRadius: '0.5rem',
              transition: 'all 0.2s ease',
              backgroundColor: 'transparent',
              color: darkMode ? '#f87171' : '#dc2626',
              border: 'none',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = darkMode ? 'rgba(239, 68, 68, 0.1)' : '#fef2f2';
              e.currentTarget.style.color = darkMode ? '#fca5a5' : '#b91c1c';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = darkMode ? '#f87171' : '#dc2626';
            }}
          >
            <LogOut size={20} style={{ marginRight: '0.75rem' }} />
            <span style={{ fontWeight: '500' }}>Cerrar Sesión</span>
          </button>
        </div>
      </motion.div>
    </>
  );
};

export default AdminSidebar;
